import i18n from "i18next";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Paragraph from "@tiptap/extension-paragraph";
import { Mathematics } from "./extensions/mathematics";
import { Footnote } from "./extensions/footnote";
import { Citation } from "./extensions/citation";
import { Reference, referenceForm } from "./extensions/reference";
import { RefPickerTrigger } from "./extensions/refPickerTrigger";
import { SlashTrigger } from "./extensions/slashTrigger";
import { WontCompileUnderline } from "./extensions/wontCompileUnderline";
import { BlockCarryExtension } from "./extensions/blockCarry";
import { BlockTypeAttr } from "./extensions/blockTypeAttr";
import { InlineUrl } from "./extensions/inline-url";
import { DateToken } from "./extensions/date-token";
import { SpacingCommand } from "./extensions/spacing-command";
import {
  Superscript,
  Subscript,
  Highlight,
  SmallCaps,
  TextColor,
  Comment,
} from "./extensions/inline-marks";
import { FontSize } from "./extensions/font-size";
import { Typography } from "./extensions/typography";
import { TextAlign } from "./extensions/text-align";
import { ParagraphAttrs } from "./extensions/paragraph-attrs";
import { getBlockNodeExtensions } from "./nodes";

export interface TiptapEditorOptions {
  content?: string;
  placeholder?: string;
  onUpdate?: (content: string) => void;
}

/**
 * Get the default extensions for the Lilia TipTap editor (per-block, legacy)
 */
export function getDefaultExtensions(options: TiptapEditorOptions = {}) {
  return [
    StarterKit.configure({
      // Disable heading as we handle it in block level
      heading: false,
      // Disable code block as we handle it in block level
      codeBlock: false,
      // Disable blockquote as we handle it in block level
      blockquote: false,
      // Disable horizontal rule
      horizontalRule: false,
      // Keep paragraph, bold, italic, strike, code
    }),
    Placeholder.configure({
      placeholder: options.placeholder || "Start typing...",
      emptyEditorClass: "is-editor-empty",
    }),
    Underline,
    Superscript,
    Subscript,
    Highlight,
    SmallCaps,
    TextColor,
    Comment,
    FontSize,
    Typography,
    TextAlign,
    ParagraphAttrs,
    Mathematics,
    Footnote,
    Citation,
    Reference,
    InlineUrl,
    DateToken,
    SpacingCommand,
  ];
}

/**
 * Get extensions for the single-document TipTap editor.
 * Enables heading natively and registers all custom atom node extensions.
 */
export function getDocumentEditorExtensions(): any[] {
  return [
    StarterKit.configure({
      heading: false, // We use our own extended Heading below
      codeBlock: false,
      blockquote: false,
      horizontalRule: false,
      paragraph: false, // We use our own extended Paragraph below
      // Lists are Lilia list blocks (listBlock). TipTap's own had no save path: a paragraph made
      // into one, or a pasted <ul>, disappeared from the document on the next save.
      bulletList: false,
      orderedList: false,
      listItem: false,
    }),
    // Extended paragraph with blockId attribute (rendered as data-block-id)
    Paragraph.extend({
      addAttributes() {
        return {
          blockId: {
            default: null,
            parseHTML: (element) => element.getAttribute("data-block-id"),
            renderHTML: (attributes) => {
              if (!attributes.blockId) return {};
              return { "data-block-id": attributes.blockId };
            },
          },
        };
      },
    }),
    // Extended heading with blockId + numbered + shortTitle attributes
    // (renders + parses via data-block-id, data-numbered, data-short-title).
    // numbered=false → \section*{} on the API side; shortTitle drives
    // \section[short]{long} for TOC.
    Heading.extend({
      addAttributes() {
        return {
          ...this.parent?.(),
          blockId: {
            default: null,
            parseHTML: (element) => element.getAttribute("data-block-id"),
            renderHTML: (attributes) => {
              if (!attributes.blockId) return {};
              return { "data-block-id": attributes.blockId };
            },
          },
          numbered: {
            default: true,
            parseHTML: (element) => element.getAttribute("data-numbered") !== "false",
            renderHTML: (attributes) => {
              if (attributes.numbered === false) return { "data-numbered": "false" };
              return {};
            },
          },
          shortTitle: {
            default: "",
            parseHTML: (element) => element.getAttribute("data-short-title") || "",
            renderHTML: (attributes) => {
              if (!attributes.shortTitle) return {};
              return { "data-short-title": attributes.shortTitle };
            },
          },
        };
      },
    }).configure({ levels: [1, 2, 3, 4] }),
    Placeholder.configure({
      placeholder: ({ node }) => {
        if (node.type.name === "heading") return i18n.t("editor:placeholder.heading");
        return i18n.t("editor:placeholder.paragraph");
      },
      includeChildren: false,
    }),
    Underline,
    Superscript,
    Subscript,
    Highlight,
    SmallCaps,
    TextColor,
    Comment,
    // FontSize required so FormatRailPanel's Size sub-view works on
    // Flow too — without it `setMark('fontSize', …)` throws "no mark
    // type named 'fontSize'" (Sentry LILIA-WEB-EDITOR-G, 2026-05-11).
    FontSize,
    Typography,
    TextAlign,
    ParagraphAttrs,
    Mathematics,
    Footnote,
    Citation,
    Reference,
    RefPickerTrigger,
    SlashTrigger,
    WontCompileUnderline,
    BlockCarryExtension,
    BlockTypeAttr,
    InlineUrl,
    DateToken,
    SpacingCommand,
    ...getBlockNodeExtensions(),
  ];
}

/**
 * Convert TipTap JSON content to plain text with LaTeX preserved
 */
export function tiptapToPlainText(json: unknown): string {
  if (!json || typeof json !== "object") return "";

  const content = json as { content?: unknown[] };
  if (!content.content || !Array.isArray(content.content)) return "";

  return content.content
    .map((node) => nodeToText(node as TiptapNode))
    .join("");
}

interface TiptapNode {
  type: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  marks?: Array<{ type: string }>;
}

function nodeToText(node: TiptapNode): string {
  if (node.type === "text") {
    return node.text || "";
  }

  if (node.type === "math") {
    const latex = node.attrs?.latex as string || "";
    return `$${latex}$`;
  }

  if (node.type === "footnote") {
    const content = node.attrs?.content as string || "";
    return `\\footnote{${content}}`;
  }

  if (node.type === "citation") {
    const citeKey = node.attrs?.citeKey as string || "";
    return `\\cite{${citeKey}}`;
  }

  if (node.type === "reference") {
    const label = node.attrs?.label as string || "";
    return `\\${referenceForm(node.attrs?.form)}{${label}}`;
  }

  if (node.type === "inlineUrl") {
    const href = node.attrs?.href as string || "";
    const text = node.attrs?.text as string || "";
    return text ? `\\href{${href}}{${text}}` : `\\url{${href}}`;
  }

  if (node.type === "paragraph") {
    const text = node.content?.map(nodeToText).join("") || "";
    return text + "\n";
  }

  if (node.content) {
    return node.content.map(nodeToText).join("");
  }

  return "";
}

/**
 * Convert plain text with LaTeX to TipTap JSON
 */
export function plainTextToTiptap(text: string): unknown {
  if (!text) {
    return {
      type: "doc",
      content: [
        {
          type: "paragraph",
          content: [],
        },
      ],
    };
  }

  const paragraphs = text.split("\n");
  const content = paragraphs.map((paragraph) => ({
    type: "paragraph",
    content: parseInlineContent(paragraph),
  }));

  return {
    type: "doc",
    content,
  };
}

/**
 * Parse inline content including math expressions, footnotes, citations, and references
 */
function parseInlineContent(text: string): unknown[] {
  const content: unknown[] = [];
  // Combined regex for math, footnotes, citations, references, and URLs
  // Supports both LaTeX-style (\cite, \ref) and Lilia-style (@cite, @ref) markers
  const inlineRegex = /(\$[^$]+\$)|(\\footnote\{([^}]+)\})|(\\cite\{([^}]+)\})|(@cite\{([^}]+)\})|(\\c?ref\{([^}]+)\})|(@ref\{([^}]+)\})|(\\url\{([^}]+)\})/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let footnoteNumber = 0;

  while ((match = inlineRegex.exec(text)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      content.push({
        type: "text",
        text: text.slice(lastIndex, match.index),
      });
    }

    if (match[1]) {
      // Math expression $...$
      const latex = match[1].slice(1, -1); // Remove $ delimiters
      content.push({
        type: "math",
        attrs: { latex },
      });
    } else if (match[2]) {
      // Footnote \footnote{...}
      footnoteNumber++;
      content.push({
        type: "footnote",
        attrs: { content: match[3], number: footnoteNumber },
      });
    } else if (match[4]) {
      // Citation \cite{key}
      content.push({
        type: "citation",
        attrs: { citeKey: match[5] },
      });
    } else if (match[6]) {
      // Citation @cite{key} (Lilia-style)
      content.push({
        type: "citation",
        attrs: { citeKey: match[7] },
      });
    } else if (match[8]) {
      // Reference \ref{label} or \cref{label} — keep which. This parser only
      // knows these two; the others stay as text here, which is lossless.
      content.push({
        type: "reference",
        attrs: { label: match[9], form: match[8].startsWith("\\cref") ? "cref" : "ref" },
      });
    } else if (match[10]) {
      // Reference @ref{label} (Lilia-style) — written back as \ref.
      content.push({
        type: "reference",
        attrs: { label: match[11], form: "ref" },
      });
    } else if (match[12]) {
      // URL \url{...}
      content.push({
        type: "inlineUrl",
        attrs: { href: match[13] },
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    content.push({
      type: "text",
      text: text.slice(lastIndex),
    });
  }

  return content;
}

export { Mathematics, Footnote, Citation, Reference, InlineUrl };
